<template>
  <!-- 编辑新手机号/邮箱/密码弹窗 -->
  <el-dialog
    :visible="visible"
    :title="dialogTitle"
    width="400px"
    :close-on-click-modal="false"
    @close="handleClose"
    append-to-body
  >
    <div class="mag-box">
      <el-form :model="safeForm" :rules="rules" ref="safeFormRef">
        <!-- 修改手机号 -->
        <el-form-item
          v-if="editType === 'phone'"
          :label="$t('accountI18n.phone')"
          prop="phone"
        >
          <el-input
            v-model="safeForm.phone"
            :placeholder="$t('accountI18n.phonePlaceholder')"
            maxlength="11"
            autocomplete="off"
          />
        </el-form-item>
        <!-- 修改邮箱 -->
        <el-form-item
          v-if="editType === 'email'"
          :label="$t('accountI18n.email')"
          prop="email"
        >
          <el-input
            v-model="safeForm.email"
            :placeholder="$t('accountI18n.emailPlaceholder')"
            autocomplete="off"
          />
        </el-form-item>
        <!-- 修改密码 -->
        <el-form-item
          v-if="editType === 'password'"
          :label="$t('accountI18n.password')"
          prop="password"
        >
          <el-input
            v-model="safeForm.password"
            type="password"
            :placeholder="$t('accountI18n.passwordPlaceholder')"
            autocomplete="new-password"
            show-password
          />
        </el-form-item>
        <el-form-item
          v-if="editType === 'password'"
          :label="$t('accountI18n.confirmPassword')"
          prop="confirmPassword"
        >
          <el-input
            v-model="safeForm.confirmPassword"
            type="password"
            :placeholder="$t('accountI18n.confirmPasswordPlaceholder')"
            autocomplete="new-password"
            show-password
          />
        </el-form-item>
        <!-- 发送验证码(修改手机号或邮箱) -->
        <el-form-item
          v-if="editType !== 'password'"
          :label="$t('accountI18n.code')"
          prop="code"
        >
          <div class="justify-between">
            <el-input
              v-model="safeForm.code"
              :placeholder="$t('accountI18n.codePlaceholder')"
              maxlength="6"
            />
            <el-button
              :disabled="countdown > 0 || !canSendCode"
              @click="sendCode"
              style="margin-left: 10px;"
            >
              {{ countdown > 0 ? `${countdown}s ${$t('accountI18n.reSendCode')}` : $t('accountI18n.codeSend') }}
            </el-button>
          </div>
        </el-form-item>
      </el-form>
    </div>
    <span slot="footer" class="dialog-footer">
      <el-button @click="handleClose">{{ $t('accountI18n.cancel') }}</el-button>
      <el-button type="primary" @click="submitForm">{{ $t('accountI18n.confirm') }}</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { mapState } from "vuex";
import {
  sendVerifyCode,
  updateAccount,
  sendEmailCode,
  logoutApi,
  forgetPassword
} from "@/api/index";
import { handleLogout } from "@/utils/request";

const PHONE_REG = /^1\d{10}$/;
const EMAIL_REG = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

export default {
  name: "EPP",
  props: {
    visible: {
      type: Boolean,
      required: true
    },
    // phone | email | password
    editType: {
      type: String,
      required: true,
      validator: v => ["phone", "email", "password"].includes(v)
    },
    // account: 已登录改绑/改密；forgot: 忘记密码
    mode: {
      type: String,
      default: "account",
      validator: v => ["account", "forgot"].includes(v)
    },
    // SafeValiate 成功后返回的 validation
    validation: {
      type: String,
      default: ""
    },
    // SafeValiate 成功后返回的手机号/邮箱（忘记密码提交用）
    contact: {
      type: String,
      default: ""
    }
  },
  data() {
    // 确认密码验证器
    const confirmPasswordValidator = (rule, value, callback) => {
      if (this.editType === "password" && value !== this.safeForm.password) {
        callback(new Error(this.$t("accountI18n.confirmPasswordError")));
      } else {
        callback();
      }
    };
    // 手机号验证器
    const phoneValidator = (rule, value, callback) => {
      if (!value || !PHONE_REG.test(value)) {
        callback(new Error(this.$t("accountI18n.phoneError")));
      } else {
        callback();
      }
    };
    // 邮箱验证器
    const emailValidator = (rule, value, callback) => {
      if (!value || !EMAIL_REG.test(value)) {
        callback(new Error(this.$t("accountI18n.emailError")));
      } else {
        callback();
      }
    };
    // 验证码验证器
    const codeValidator = (rule, value, callback) => {
      if (!value || value.length !== 6) {
        callback(new Error(this.$t("accountI18n.codeError")));
      } else {
        callback();
      }
    };
    return {
      safeForm: {
        phone: "",
        email: "",
        password: "",
        confirmPassword: "",
        code: ""
      },
      countdown: 0,
      timer: null,
      rules: {
        phone: [{ validator: phoneValidator, trigger: "blur" }],
        email: [{ validator: emailValidator, trigger: "blur" }],
        code: [{ validator: codeValidator, trigger: "blur" }],
        password: [
          {
            required: true,
            message: this.$t("accountI18n.passwordError"),
            trigger: "blur"
          }
        ],
        confirmPassword: [{ required: true, validator: confirmPasswordValidator, trigger: "blur" }]
      }
    };
  },
  computed: {
    // 计算弹窗标题
    ...mapState(["userInfo"]),
    dialogTitle() {
      const map = {
        phone: this.$t("accountI18n.phone"),
        email: this.$t("accountI18n.email"),
        password: this.$t("accountI18n.password")
      };
      return map[this.editType] || this.$t("accountI18n.password");
    },
    canSendCode() {
      if (this.editType === "phone") {
        return PHONE_REG.test(this.safeForm.phone || "");
      }
      if (this.editType === "email") {
        return EMAIL_REG.test(this.safeForm.email || "");
      }
      return false;
    }
  },
  watch: {
    // 监听弹窗是否显示
    visible(val) {
      if (val) {
        this.reset();
      } else {
        this.clearTimer();
        this.countdown = 0;
      }
    }
  },
  methods: {
    // 开始倒计时
    startCountdown() {
      this.countdown = 60;
      this.clearTimer();
      this.timer = setInterval(() => {
        this.countdown--;
        if (this.countdown <= 0) {
          this.clearTimer();
        }
      }, 1000);
    },
    // 清除倒计时
    clearTimer() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },
    // 发送验证码
    sendCode() {
      if (this.editType === "password" || !this.canSendCode || this.countdown > 0) return;
      const field = this.editType === "phone" ? "phone" : "email";
      this.$refs.safeFormRef.validateField(field, errorMessage => {
        if (errorMessage) return;
        const request =
          this.editType === "phone"
            ? sendVerifyCode({ phone: this.safeForm.phone })
            : sendEmailCode({ email: this.safeForm.email });
        request
          .then(res => {
            if (res.data.code == 200) {
              this.startCountdown();
              this.$message.success(this.$t("accountI18n.codeSendSuccess"));
            } else {
              this.$message.error(res.data.data || this.$t("accountI18n.codeSendError"));
            }
          })
          .catch(err => {
            this.$message.error(err || this.$t("accountI18n.codeSendError"));
          });
      });
    },
    // 提交表单
    submitForm() {
      this.$refs.safeFormRef.validate(valid => {
        if (!valid) return;
        if (this.editType === "phone") {
          this.updatePhone();
        } else if (this.editType === "email") {
          this.updateEmail();
        } else if (this.mode === "forgot") {
          this.resetForgotPassword();
        } else {
          this.updatePassword();
        }
      });
    },
    // 修改手机号
    updatePhone() {
      updateAccount({
        phone: this.safeForm.phone,
        security_code: this.safeForm.code,
        validation: this.validation
      })
        .then(res => {
          if (res.data.code == 200) {
            this.$message.success(this.$t("accountI18n.phoneSuccess"));
            this.emitSuccess();
          } else {
            this.$message.error(res.data.data);
          }
        })
        .catch(err => {
          this.$message.error(err || this.$t("accountI18n.updateError"));
        });
    },
    // 修改邮箱
    updateEmail() {
      updateAccount({
        email: this.safeForm.email,
        security_code: this.safeForm.code,
        validation: this.validation
      })
        .then(res => {
          if (res.data.code == 200) {
            this.$message.success(this.$t("accountI18n.emailSuccess"));
            this.emitSuccess();
          } else {
            this.$message.error(res.data.data);
          }
        })
        .catch(err => {
          this.$message.error(err || this.$t("accountI18n.updateError"));
        });
    },
    // 忘记密码
    resetForgotPassword() {
      forgetPassword(this.contact, {
        password: this.safeForm.password,
        validation: this.validation
      })
        .then(res => {
          if (res.data.code == 200) {
            this.$message.success(this.$t("accountI18n.updateSuccess"));
            this.emitSuccess();
          } else {
            this.$message.error(res.data.data);
          }
        })
        .catch(err => {
          this.$message.error(err || this.$t("accountI18n.updateError"));
        });
    },
    // 修改密码
    updatePassword() {
      updateAccount({
        password: this.safeForm.password,
        validation: this.validation
      })
        .then(res => {
          if (res.data.code == 200) {
            this.$message.success(this.$t("accountI18n.updateSuccess"));
            setTimeout(() => {
              logoutApi()
                .then(logoutRes => {
                  if (logoutRes.data.code === 200) {
                    handleLogout();
                    setTimeout(() => {
                      window.location.reload();
                    }, 1000);
                  } else {
                    this.$message.error(logoutRes.data.data);
                  }
                })
                .catch(error => {
                  this.$message.error(error || this.$t("accountI18n.logoutError"));
                });
            }, 1000);
          } else {
            this.$message.error(res.data.data);
          }
        })
        .catch(err => {
          this.$message.error(err || this.$t("accountI18n.updateError"));
        });
    },
    // 编辑成功
    emitSuccess() {
      this.$emit("success");
      this.$emit("update:visible", false);
      this.reset();
    },
    // 关闭弹窗
    handleClose() {
      this.$emit("update:visible", false);
      this.reset();
    },
    // 重置表单
    reset() {
      this.clearTimer();
      this.countdown = 0;
      this.safeForm = { phone: "", email: "", password: "", code: "" };
      this.$nextTick(() => {
        this.$refs.safeFormRef && this.$refs.safeFormRef.clearValidate();
      });
    }
  },
  beforeDestroy() {
    this.clearTimer();
  }
};
</script>
